/**
 * Свой маленький клиент ActionCable (без npm-библиотеки @rails/actioncable).
 *
 * Почему свой: протокол — пять видов JSON-сообщений, а нам нужны только подписка на один канал,
 * переподключение с нарастающей паузой и «сторож тишины». Библиотека же тянет свой таймер и
 * сама решает, когда переподключаться, — а в оболочке телефона после сна приложения ей
 * приходится помогать вручную (reconnectNow), и проще держать это в одном месте.
 *
 * Протокол (как у панели Chatwoot, app/javascript/shared/helpers/BaseActionCableConnector.js):
 *   wss://<chatwoot>/cable, подпротокол actioncable-v1-json
 *   ← {type:'welcome'}                         → шлём {command:'subscribe', identifier}
 *   ← {type:'ping', message:<время>}           раз в 3 с (по нему видим, что канал жив)
 *   ← {type:'confirm_subscription'|'reject_subscription'}
 *   ← {identifier, message:{event, data}}      само событие
 *   ← {type:'disconnect', reason, reconnect}
 * identifier = JSON {"channel":"RoomChannel","pubsub_token":…,"account_id":…,"user_id":…}
 * (app/channels/room_channel.rb: pubsub_token + user_id проверяются, account_id — членство).
 * Происхождение страницы не проверяется: config/initializers/cors.rb →
 * disable_request_forgery_protection = true, поэтому подходят и capacitor://localhost, и localhost.
 */

export type CableState = 'connecting' | 'open' | 'closed';

export interface CableIdentifier {
  channel: 'RoomChannel';
  pubsub_token: string;
  account_id: number;
  user_id: number;
}

export interface CableOptions {
  url: string;
  identifier: CableIdentifier;
  onEvent: (event: string, data: unknown) => void;
  onState?: (state: CableState) => void;
  /** Подписка подтверждена ПОСЛЕ обрыва (или повторно) — пора дозагрузить пропущенное. */
  onReconnected?: () => void;
  /** Сервер отклонил подписку (протух pubsub_token) — вызывающий обновит профиль. */
  onRejected?: () => void;
  /** Подмена для проверок. */
  WebSocketImpl?: typeof WebSocket;
  now?: () => number;
  setIntervalImpl?: typeof setInterval;
  clearIntervalImpl?: typeof clearInterval;
  setTimeoutImpl?: typeof setTimeout;
  clearTimeoutImpl?: typeof clearTimeout;
}

/** Пауза перед попыткой номер attempt (с нуля): 1, 2, 4, 8, 16, 30 с и дальше 30 с, плюс до 20% «дрожи». */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(30_000, 1000 * 2 ** Math.max(0, attempt));
  return Math.round(base * (1 + 0.2 * random()));
}

/** Сервер шлёт ping раз в 3 с; нет ничего дольше этого — канал мёртв (как у штатного клиента: 6 с, берём с запасом). */
export const STALE_AFTER_MS = 8_000;
const WATCHDOG_MS = 2_000;

export class Cable {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private lastSeen = 0;
  private wanted = false;
  private everSubscribed = false;
  private wasDown = false;
  private state: CableState = 'closed';
  private readonly o: CableOptions;
  private readonly now: () => number;

  constructor(options: CableOptions) {
    this.o = options;
    this.now = options.now ?? Date.now;
  }

  getState(): CableState {
    return this.state;
  }

  /** Подключиться и держать связь. */
  start(): void {
    this.wanted = true;
    this.connect();
  }

  /** Отключиться совсем (выход, смена кабинета). */
  stop(): void {
    this.wanted = false;
    this.clearTimers();
    this.detach();
    this.setState('closed');
  }

  /**
   * Приложение вернулось на экран или сеть появилась: если связь не «открыта» — подключаемся
   * немедленно, не дожидаясь паузы; если открыта, но мы долго молчали — считаем её сомнительной.
   */
  reconnectNow(): void {
    if (!this.wanted) return;
    if (this.state === 'open' && this.now() - this.lastSeen < STALE_AFTER_MS) return;
    this.clearTimers();
    this.detach();
    this.attempt = 0;
    this.connect();
  }

  private setState(s: CableState): void {
    if (this.state === s) return;
    this.state = s;
    this.o.onState?.(s);
  }

  private connect(): void {
    if (!this.wanted) return;
    if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) return;
    const Impl = this.o.WebSocketImpl ?? WebSocket;
    this.setState('connecting');
    let ws: WebSocket;
    try {
      ws = new Impl(this.o.url, ['actioncable-v1-json', 'actioncable-unsupported']);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    this.lastSeen = this.now();
    ws.onmessage = (ev) => this.onMessage(ws, ev);
    ws.onclose = () => this.onClose(ws);
    ws.onerror = () => { /* за ошибкой всегда следует close — реагируем там */ };
    const setI = this.o.setIntervalImpl ?? setInterval;
    this.watchdog = setI(() => {
      if (this.now() - this.lastSeen > STALE_AFTER_MS) {
        // Тишина: канал завис (обычно после сна телефона). Закрываем — onClose переподключит.
        try { ws.close(); } catch { /* уже закрыт */ }
        this.onClose(ws);
      }
    }, WATCHDOG_MS);
  }

  private onMessage(ws: WebSocket, ev: MessageEvent): void {
    if (ws !== this.ws) return;
    this.lastSeen = this.now();
    let msg: { type?: string; identifier?: string; message?: unknown; reconnect?: boolean };
    try {
      msg = JSON.parse(String(ev.data));
    } catch {
      return;
    }
    switch (msg.type) {
      case 'welcome':
        ws.send(JSON.stringify({ command: 'subscribe', identifier: JSON.stringify(this.o.identifier) }));
        return;
      case 'ping':
        return;
      case 'confirm_subscription': {
        this.attempt = 0;
        this.setState('open');
        const again = this.everSubscribed || this.wasDown;
        this.everSubscribed = true;
        this.wasDown = false;
        if (again) this.o.onReconnected?.();
        return;
      }
      case 'reject_subscription':
        this.o.onRejected?.();
        return;
      case 'disconnect':
        try { ws.close(); } catch { /* уже закрыт */ }
        return;
      default:
        break;
    }
    const m = msg.message;
    if (m && typeof m === 'object' && 'event' in m && typeof (m as { event: unknown }).event === 'string') {
      const { event, data } = m as { event: string; data: unknown };
      this.o.onEvent(event, data);
    }
  }

  private onClose(ws: WebSocket): void {
    if (ws !== this.ws) return;
    this.detach();
    this.wasDown = true;
    this.setState('closed');
    if (this.wanted) this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || !this.wanted) return;
    const delay = backoffDelay(this.attempt);
    this.attempt += 1;
    const setT = this.o.setTimeoutImpl ?? setTimeout;
    this.reconnectTimer = setT(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private detach(): void {
    const ws = this.ws;
    this.ws = null;
    if (this.watchdog !== null) {
      (this.o.clearIntervalImpl ?? clearInterval)(this.watchdog);
      this.watchdog = null;
    }
    if (ws) {
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try { ws.close(); } catch { /* уже закрыт */ }
    }
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== null) {
      (this.o.clearTimeoutImpl ?? clearTimeout)(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
